/* eslint-disable react/no-array-index-key */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import TBody from '../../components/table/TBody/TBody';
import TD from '../../components/table/TD/TD';
import TR from '../../components/table/TR/TR';

import Search from '../../components/search/Search';
import Table from '../../components/table/Table';
import { ProjectSortTypes } from '../../core/enums/ProjectSortTypes';
import { getProjects } from '../../core/state/project/asyncActions';
import { Project } from '../../core/state/project/types';
import { RootState } from '../../core/state/state';
import { projectListSorter } from '../../core/utils/projectListSorter';
import Image from '../../components/image/Image';
import { BASE_URL } from '../../core/constants/api';
import ProjectListingHeader from './ProjectListingHeader';
import { history } from '../../core/state/history';
import TableWrapper from '../components/table-wrapper/TableWrapper';
import NoResultsPage from '../../components/no-results-page/NoResultsPage';
import { DATE_FORMAT_EU_DOT } from '../../core/constants/date';
import { getHighlightedText } from './getHighlightedText';

import './ProjectListing.scss';

const ProjectListing: React.FC = () => {
  const [sortFilter, setSortFilter] = React.useState<ProjectSortTypes>(ProjectSortTypes.ProjectNumber);
  const [isSortAscending, setIsSortAscending] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState('');
  const { projects, projectsCopy: projectList } = useSelector((state: RootState) => state.projects);
  const noPermissions = projects && projectList?.length === 0;

  const { t } = useTranslation();
  const dispatch = useDispatch();

  React.useEffect(() => {
    dispatch(getProjects(true));
  }, [dispatch]);

  const handleSorting = (type: ProjectSortTypes) => {
    const sortDirection = type === sortFilter ? !isSortAscending : true;
    setIsSortAscending(sortDirection);
    setSortFilter(type);
    if (projectList) {
      projectListSorter(projectList, sortDirection ? 'asc' : 'desc', type, search);
    }
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    if (projectList) {
      if (value.length > 2) {
        projectListSorter(projectList, undefined, undefined, value);
      } else {
        projectListSorter(projectList, 'desc', ProjectSortTypes.ProjectNumber);
      }
    }
  };

  return (
    <TableWrapper>
      <div className="table__header">
        <div className="d-flex">
          <span>{search.length > 2 ? t('projectListing.header.searchResults') : t('projectListing.header.title')}</span>
          <div className="project-listing__header-count">{projects?.length || 0}</div>
        </div>
        {!noPermissions && (
          <Search
            id="project-search"
            name="project-search"
            value={search}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
            onReset={() => handleSearch('')}
          />
        )}
      </div>
      <div className="project-listing__table">
        <Table>
          <ProjectListingHeader isSortAscending={isSortAscending} sortFilter={sortFilter} handleSorting={handleSorting} />
          <TBody>
            {projects?.map((project: Project, i) => (
              <TR className="project-listing__table-item" key={`${i}-${project.id}`} onClick={() => history.push(`/projects/${project.id}/bookings`)}>
                <TD className="d-flex align-items-center">
                  <Image className="project-listing__project-img" src={`${BASE_URL}/${project.photo}`} alt="" />
                  <div className="font-weight-bold">{getHighlightedText(project.name, search)}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">{getHighlightedText(project.code, search)}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">{getHighlightedText(project.constructionArea, search)}</div>
                </TD>
                <TD className="text-left">
                  <div className="table__text">{project.lastBooking ? format(new Date(project.lastBooking), DATE_FORMAT_EU_DOT) : '--'}</div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {noPermissions && (
          <NoResultsPage title={t('projectListing.noPermissions.title')} description={t('projectListing.noPermissions.subtitle')} icon="stop" />
        )}
        {!projects && (
          <NoResultsPage
            title={search.length > 2 ? t('projectListing.noResults.title') : t('common.noProjects')}
            description={search.length > 2 ? t('projectListing.noResults.text') : t('projectListing.noProjects.text')}
            icon={search.length > 2 ? 'search' : 'projects'}
          />
        )}
      </div>
    </TableWrapper>
  );
};

export default ProjectListing;
